import { createFileRoute } from '@tanstack/react-router';
import { Check, X } from "lucide-react";
import { ContentHeader, ContentSection, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/comparisons/langfuse')({
  component: LangfuseComparisonPage,
  head: () => ({
    meta: [
      { title: 'Matricks vs Langfuse | Matricks' },
      { name: 'description', content: 'Compare Matricks and Langfuse for LLM observability and management. See how our AI gateway capabilities differ.' },
    ],
  }),
});

const comparisonData = [
  { feature: "LLM Observability", matricks: true, langfuse: true },
  { feature: "Tracing & Logging", matricks: true, langfuse: true },
  { feature: "Cost Tracking", matricks: true, langfuse: true },
  { feature: "Prompt Management", matricks: true, langfuse: true },
  { feature: "API Gateway", matricks: true, langfuse: false },
  { feature: "Model Routing", matricks: true, langfuse: false },
  { feature: "Rate Limiting", matricks: true, langfuse: false },
  { feature: "2000+ Integrations", matricks: true, langfuse: false },
  { feature: "AI Agent Platform", matricks: true, langfuse: false },
  { feature: "Workflow Automation", matricks: true, langfuse: false },
  { feature: "Enterprise Security", matricks: true, langfuse: "Basic" },
];

const faqItems = [
  {
    question: "Is Matricks a replacement for Langfuse?",
    answer: "Matricks includes all the LLM observability features of Langfuse, plus API gateway, integrations, and agent platform. If you need more than just observability, Matricks is a more complete solution.",
  },
  {
    question: "Can I use both together?",
    answer: "While possible, it's usually unnecessary. Matricks provides comprehensive LLM observability alongside its other features. Using both would create redundant tracking.",
  },
  {
    question: "How does tracing compare?",
    answer: "Both provide detailed request tracing. Matricks traces across the entire request lifecycle including API gateway, integrations, and AI calls. Langfuse focuses specifically on LLM interactions.",
  },
];

function LangfuseComparisonPage() {
  return (
    <div>
      <ContentHeader
        title="Matricks vs Langfuse"
        description="Langfuse is an open-source LLM observability platform. Here's how Matricks compares with our broader AI gateway approach."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Comparisons", href: "/comparisons" },
          { label: "vs Langfuse" },
        ]}
      />

      {/* Overview */}
      <ContentSection id="overview" title="Overview">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold text-lg">Langfuse</h3>
            <p className="text-muted-foreground mt-2">
              Langfuse is an open-source LLM observability platform. It provides tracing, prompt
              management, and cost tracking for LLM applications. Best for teams focused
              specifically on LLM monitoring and debugging.
            </p>
          </div>
          <div className="rounded-xl border bg-primary/5 border-primary/20 p-6">
            <h3 className="font-semibold text-lg">Matricks</h3>
            <p className="text-muted-foreground mt-2">
              Matricks is a complete integration platform with AI gateway capabilities. Beyond
              observability, it provides model routing, API management, 2000+ integrations,
              and an AI agent platform. Best for teams building production AI applications.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* Comparison Table */}
      <ContentSection
        id="comparison"
        title="Feature Comparison"
        background="muted"
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="py-4 px-4 text-left font-semibold">Feature</th>
                <th className="py-4 px-4 text-center font-semibold bg-primary/5 text-primary">Matricks</th>
                <th className="py-4 px-4 text-center font-semibold">Langfuse</th>
              </tr>
            </thead>
            <tbody>
              {comparisonData.map((row) => (
                <tr key={row.feature} className="border-b last:border-0">
                  <td className="py-4 px-4">{row.feature}</td>
                  <td className="py-4 px-4 text-center bg-primary/5">
                    {row.matricks === true ? (
                      <Check className="h-5 w-5 text-green-500 mx-auto" />
                    ) : row.matricks === false ? (
                      <X className="h-5 w-5 text-red-500 mx-auto" />
                    ) : (
                      <span className="text-sm">{row.matricks}</span>
                    )}
                  </td>
                  <td className="py-4 px-4 text-center">
                    {row.langfuse === true ? (
                      <Check className="h-5 w-5 text-green-500 mx-auto" />
                    ) : row.langfuse === false ? (
                      <X className="h-5 w-5 text-red-500 mx-auto" />
                    ) : (
                      <span className="text-sm text-muted-foreground">{row.langfuse}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ContentSection>

      {/* Key Differences */}
      <ContentSection id="differences" title="Key Differences">
        <div className="space-y-6">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Scope of Platform</h3>
            <p className="text-muted-foreground mt-2">
              Langfuse focuses on LLM observability. Matricks provides a complete platform
              including API gateway, integrations, workflow automation, and AI agents.
              You get observability as part of a larger solution.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">AI Gateway Features</h3>
            <p className="text-muted-foreground mt-2">
              Matricks includes model routing, failover, caching, and rate limiting for AI
              requests. Langfuse observes but doesn't control AI traffic. For production
              AI applications, the gateway features are essential.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Integration Ecosystem</h3>
            <p className="text-muted-foreground mt-2">
              Matricks connects your AI applications to 2000+ external services. Langfuse
              focuses on the AI layer only. For applications that need to interact with
              CRMs, databases, and other services, Matricks provides the full stack.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Get More Than Observability"
        description="Try Matricks for complete AI application management."
        primaryAction={{ label: "Start Free Trial", href: "/login" }}
        secondaryAction={{ label: "View AI Gateway", href: "/features/ai-gateway" }}
        background="primary"
      />
    </div>
  );
}
